import {
  Breadcrumb,
  Col,
  Container,
  Row,
  Tab,
  Nav,
  Button,
  Collapse,
} from "react-bootstrap";
import { useState, useRef, useEffect } from "react";
import { useRouter } from 'next/navigation';
import Head from 'next/head';

import BreadcrumbItem from "../../BreadcrumbItem";
import ChartBoard from "../../Company/ChartBoard";
import CompanyFAQ from "../../Company/CompanyFAQ";
import CompanyListGroup from "../../Company/CompanyListGroup";
import CompanyList from "../../Company/CompanyTable";
import { toTitleCase } from "../../../helpers/to-title-case";
import { parseMarkdown } from "../../../helpers/parse-markdown";
import { Info, ArrowRight } from "lucide-react";

const IntroText = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(true);
  const textRef = useRef(null);
  const [height, setHeight] = useState('4.8em');

  if (!text) return null;

  const toggleExpanded = () => {
    if (!isExpanded) {
            setIsClamped(false);
      
      setTimeout(() => {
        if (textRef.current) {
          setHeight(`${textRef.current.scrollHeight}px`);
        }
      }, 0);
    } else {
      setHeight('4.8em');
    }
    setIsExpanded(!isExpanded);
  };

  const handleTransitionEnd = () => {
    if (!isExpanded) {
      setIsClamped(true);
    }
  };

  const style = {
    lineHeight: '1.6',
    maxHeight: height,
    transition: 'max-height 0.4s ease-in-out',
    overflow: 'hidden',
    ...(isClamped ? {
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
    } : {})
  };

  const paragraphs = text.trim().split(/\n\s*\n/);

  return (
    <div className="mb-0">
      <div 
        ref={textRef}
        className="text-gray-900 text-sm last-p-mb-0" 
        style={style}
        onTransitionEnd={handleTransitionEnd}
      >
        {paragraphs.map((para, idx) => (
          <p 
            key={idx} 
            className={isClamped ? "mb-0" : "mb-3"}
            dangerouslySetInnerHTML={{ __html: parseMarkdown(para.trim().replace(/\n/g, '<br />')) }} 
          />
        ))}
      </div>
      <Button
        variant="link"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        className="btn-collapse ps-0 py-0 text-lowercase fw-bold shadow-none mt-2"
        style={{ fontSize: '0.875rem' }}
      >
        {isExpanded ? 'Read less' : 'Read more'}
      </Button>
    </div>
  );
};

const CompaniesPage = (props) => {
  const { slug, linkage, headlines, initialData, faqData, hasTrendsData, updatedDate, updatedDateISO } = props;
  const hasFaqQuestions = !!(faqData && faqData.questions && faqData.questions.length > 0);

  const [activeTab, setActiveTab] = useState("0");

  const data = (initialData.length > 15) ? initialData.slice(0, 15) : initialData;

  const tabList = ['Index', 'Leaders', 'Trends & stats'];

  const router = useRouter();

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#leaders-board") {
        setActiveTab("1");
      } else {
        setActiveTab("0");
      }
    };

    // Run on mount
    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const handleTabSelect = (k, e) => {
    if (k === "2") {
      e.preventDefault();
      router.push(`${linkage.base_url}/trends`);
      return;
    }
    
    if (k === "1") {
      window.location.hash = "leaders-board";
    } else if (k === "0") {
      if (window.location.hash) {
        window.history.pushState("", document.title, window.location.pathname + window.location.search);
      }
    }
    setActiveTab(k);
  };

  const getLocationLabel = (code, locationData) => {
    const location = locationData?.find(item => item.value.toLowerCase() === code.toLowerCase());
    return location ? location.label : code;
  };

  const getCountryLabel = (code) => getLocationLabel(code, props.catalog?.location);
  const getStateLabel = (code) => getLocationLabel(code, props.catalog?.states);

  const baseUrl = "https://vendorland.com";
  const canonicalUrl = `${baseUrl}${linkage.base_url || ''}`;
  const leadersList = initialData ? initialData.slice(0, 15) : [];

  let pageLocation = "Worldwide";
  if (linkage.country) {
    const countryLabel = getCountryLabel(linkage.country);
    pageLocation = countryLabel ? toTitleCase(countryLabel) : linkage.country;
    
    if (linkage.state) {
      const stateLabel = getStateLabel(linkage.state);
      pageLocation = `${stateLabel ? toTitleCase(stateLabel) : linkage.state}, ${pageLocation}`;
    }
    if (linkage.city) {
      pageLocation = `${toTitleCase(linkage.city)}, ${pageLocation}`;
    }
  }

  const categoryName = toTitleCase(linkage.label || '');

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": headlines.mainTitle || `Top ${categoryName} Companies`,
    "description": props.meta || `Top 15 ${categoryName} Companies ranking list on Vendorland.`,
    "url": canonicalUrl,
    "numberOfItems": leadersList.length,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl,
      "dateModified": updatedDateISO,
      "publisher": {
        "@type": "Organization",
        "name": "Vendorland",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/favicon.png`
        }
      }
    },
    "itemListElement": leadersList.map((item, index) => {
      const companySlug = item.vendor_url || '';
      const companyUrl = companySlug ? (companySlug.startsWith('http') ? companySlug : `${baseUrl}/company/${companySlug}`) : '';
      
      let logoUrl = '';
      if (item.logo_url) {
        logoUrl = item.logo_url.startsWith('http') 
          ? item.logo_url 
          : `${baseUrl}/vl-api/files/${item.logo_url}`;
      }

      const listItem = {
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Organization",
          "name": item.title || '',
          "url": companyUrl,
          "description": item.description || `Learn about ${item.title} services, rating, portfolio, and client reviews on Vendorland.`,
          "knowsAbout": [categoryName],
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": pageLocation
          }
        }
      };

      if (logoUrl) {
        listItem.item.logo = logoUrl;
      }

      if (item.website_url && item.website_url !== '/') {
        listItem.item.sameAs = [item.website_url];
      }

      if (item.founded) {
        listItem.item.foundingDate = String(item.founded);
      }

      const city = item.location_city || '';
      const country = item.location_country?.label || item.location_country?.value || '';
      const location = `${city}, ${country}`.replace(/^,\s*|,\s*$/g, '').trim();
      if (location) {
        listItem.item.address = {
          "@type": "PostalAddress",
          "addressLocality": location
        };
      }

      return listItem;
    })
  };

  return (
    <section className="py-5 px-auto">
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      </Head>
      <Container>
        <Row>
          <Col sm={8}>
            <Breadcrumb listProps={{ className: "pl-0  justify-content-start" }}>
              <BreadcrumbItem href="/">Home</BreadcrumbItem>
              <BreadcrumbItem href="/categories" >Categories</BreadcrumbItem>
              {linkage.parent_label && linkage.parent_label !== linkage.label && 
                <BreadcrumbItem href={`/companies${linkage.parent_linkage}`}>{toTitleCase(linkage.parent_label)}</BreadcrumbItem>
              }
              
              {linkage.country && (linkage.state || linkage.city) &&
                <BreadcrumbItem href={`/companies/${slug[0]}/${linkage.country}`}>
                  {toTitleCase(getCountryLabel(linkage.country))}
                </BreadcrumbItem>
              }
              
              {linkage.state && linkage.city &&
                <BreadcrumbItem href={`/companies/${slug[0]}/${linkage.country}/${linkage.state}`}>
                  {toTitleCase(getStateLabel(linkage.state))}
                </BreadcrumbItem>
              }
              
              <BreadcrumbItem active>{toTitleCase(linkage.label)}</BreadcrumbItem>
            </Breadcrumb>
          </Col>
          <Col className="d-flex justify-content-sm-end justify-content-start mt-3 mb-3 mb-sm-0 text-muted text-xs text-uppercase">
            {updatedDate}
          </Col>
        </Row>
        <h1 className="hero-heading mb-4 text-capitalize">{headlines.mainTitle}</h1>
        <Row className="align-items-start mb-4">
          <Col lg="7" xl="8" className="mb-4 mb-lg-0">
            <div className="pt-4 pb-0 pe-4 ps-0 bg-white rounded-0">
              {faqData && faqData.intro && (
                <IntroText text={faqData.intro} />
              )}
              {linkage.value === 'CSD' && !linkage.item && (
                <div dangerouslySetInnerHTML={{ __html: data.content }} />
              )}
            </div>
          </Col>
          <Col lg="5" xl="4">
            <div className="p-4 bg-gray-100 rounded-0 d-flex align-items-start">
              <Info size={20} className="me-3 mt-1 flex-shrink-0" style={{ color: '#6c757d' }} />
              <div className="flex-grow-1">
                <p className="text-sm mb-0 text-gray-900" style={{ lineHeight: '1.6' }}>
                  Vendorland Scores are based on service specialization, verified reviews, company experience, and market reputation.
                  {' '}
                  <a 
                    href="https://vendorland.com/methodology" 
                    className="fw-bold text-primary text-hover-underline d-inline" 
                    style={{ fontSize: '0.875rem' }}
                  >
                    Learn how Vendorland Scores are calculated <ArrowRight className="ms-1 d-inline-block align-middle" size={13} />
                  </a>
                </p>
              </div>
            </div>
          </Col>
        </Row>
        <Tab.Container activeKey={activeTab}>
          <Nav variant="tabs" className="justify-content-end fw-bold mb-4" onSelect={handleTabSelect}>
            <Nav.Item>
              <Nav.Link eventKey="0">{tabList[0]}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="1">{tabList[1]}</Nav.Link>
            </Nav.Item>
            {hasTrendsData && (
              <Nav.Item>
                <Nav.Link
                  eventKey="2"
                  onClick={(e) => {
                    e.preventDefault();
                    router.push(`${linkage.base_url}/trends`);
                  }}
                >
                  {tabList[2]}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="0">
              <CompanyList props={props} className={hasFaqQuestions ? "pt-5" : "py-5"} />
              {hasFaqQuestions && <CompanyFAQ className="mt-5 py-5 border-top" {...props} />}
            </Tab.Pane>
            <Tab.Pane eventKey="1">
              <ChartBoard className="py-0" title={`${toTitleCase(linkage.label)} Leaders Board`} data={data} />
              <CompanyListGroup className="py-5" {...props} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </section>
  )
}

export default CompaniesPage;